import { Logger } from './logger';
import { buildDocsHref } from './link-navigation';
import { keyboardShortcutHtml, shortcutSpecFromHandbook } from './keyboard-shortcut-display';
import { getDocsPageId, getDocsWidthPx, getFocusViewId, isDocsOpen, saveWindowUiFromDom, setDocsPageId } from './window-ui-state';
const console = new Logger('DocsViewer');
const HANDBOOK_ROOT = '/handbook';
const DEFAULT_WIDTH = 340;
const MIN_WIDTH = 200;
function slugify(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
function docsHeadingId(raw) {
    const slug = raw.replace(/^docs-/, '');
    return slug ? `docs-${slug}` : '';
}
function usesCommandModifier() {
    return navigator.platform.toUpperCase().includes('MAC');
}
function isLightAppTheme() {
    return document.documentElement.getAttribute('data-theme') === 'light';
}
function canonicalHandbookImageSrc(src) {
    return src.replace(/-dark(\.[a-z0-9]+)$/i, '$1');
}
function darkHandbookImageSrc(src) {
    const canonical = canonicalHandbookImageSrc(src);
    return canonical.replace(/(\.[a-z0-9]+)$/i, '-dark$1');
}
function themedHandbookImageSrc(src) {
    const canonical = canonicalHandbookImageSrc(src);
    if (/^(https?:|data:)/i.test(canonical)) {
        return canonical;
    }
    if (!/\.(png|jpe?g|webp|gif)$/i.test(canonical)) {
        return canonical;
    }
    return isLightAppTheme() ? canonical : darkHandbookImageSrc(canonical);
}
function localizeModifierNotation(markdown) {
    const command = usesCommandModifier() ? 'Cmd' : 'Ctrl';
    const option = usesCommandModifier() ? 'Option' : 'Alt';
    return markdown
        .replaceAll('Cmd/Ctrl', command)
        .replaceAll('Alt/Option', option);
}
function flattenPages(nodes, into) {
    for (const node of nodes) {
        if (node.kind === 'page' && node.path) {
            into.set(node.id, {
                id: node.id,
                title: node.title,
                path: node.path
            });
        }
        if (node.children) {
            flattenPages(node.children, into);
        }
    }
}
function handbookUrl(relativePath) {
    return `${HANDBOOK_ROOT}/${relativePath.replace(/^\/+/, '')}`;
}
export class DocsViewer {
    constructor() {
        this.manifest = null;
        this.pages = new Map();
        this.currentId = null;
        this.loadPromise = null;
        this.animationTimer = null;
        const shell = document.getElementById('app-shell');
        const view = document.getElementById('view-docs');
        const divider = document.getElementById('docs-divider');
        const tocEl = document.getElementById('docs-toc');
        const articleEl = document.getElementById('docs-article');
        const closeBtn = document.getElementById('docs-close-btn');
        if (!shell || !view || !divider || !tocEl || !articleEl || !closeBtn) {
            throw new Error('Documentation viewer markup is missing');
        }
        this.shell = shell;
        this.view = view;
        this.tocEl = tocEl;
        this.articleEl = articleEl;
        this.closeBtn = closeBtn;
        this.view.style.flex = '0 0 0px';
        this.closeBtn.addEventListener('click', () => this.close());
        this.articleEl.addEventListener('click', (event) => this.onArticleClick(event));
        void this.restorePersistedOpen();
    }
    isOpen() {
        return this.shell.classList.contains('docs-open');
    }
    restorePersistedOpen() {
        if (!this.readSavedOpen()) {
            return;
        }
        if (!this.isOpen()) {
            this.setColumnOpen(true, { animate: false, persist: false });
        }
        void this.loadPersistedPage();
    }
    refreshCodeHighlight() {
        this.highlightCodeBlocks();
    }
    refreshThemedImages() {
        this.applyThemedImages();
    }
    async open(id, heading) {
        await this.ensureManifest();
        const targetId = id ||
            this.readLastPageId() ||
            this.manifest?.defaultId ||
            this.pages.keys().next().value;
        if (!targetId) {
            console.error('Documentation manifest has no pages');
            return;
        }
        if (!this.isOpen()) {
            this.setColumnOpen(true);
        }
        window.focusView?.('view-docs');
        await this.showPage(targetId, heading);
        // showPage / layout work can race the focusView lock; ensure Docs ends
        // focused after the page is on screen (Cmd+Escape and chrome depend on it).
        if (!this.view.classList.contains('focused') ||
            window.getCurrentFocusedView?.() !== 'view-docs') {
            window.focusView?.('view-docs');
        }
        this.scrollCurrentTocIntoView();
        requestAnimationFrame(() => this.scrollCurrentTocIntoView());
    }
    close() {
        if (!this.isOpen()) {
            return;
        }
        const docsWereFocused = this.view.classList.contains('focused') ||
            window.getCurrentFocusedView?.() === 'view-docs';
        this.setColumnOpen(false);
        if (!docsWereFocused) {
            return;
        }
        const lastActiveView = getFocusViewId();
        window.focusView?.(lastActiveView && lastActiveView !== 'view-docs'
            ? lastActiveView
            : 'view-editor');
    }
    animationConfig() {
        const animation = window.VIEW_SETTINGS?.animation;
        return {
            enabled: animation?.enabled !== false,
            duration: typeof animation?.duration === 'number'
                ? animation.duration
                : 300,
            easing: typeof animation?.easing === 'string'
                ? animation.easing
                : 'ease-in-out'
        };
    }
    setColumnOpen(open, options) {
        const persist = options?.persist !== false;
        const { enabled, duration, easing } = this.animationConfig();
        const animate = options?.animate !== false && enabled;
        const divider = document.getElementById('docs-divider');
        const container = this.shell.querySelector('.container');
        const animated = [this.view, divider, container].filter((element) => Boolean(element));
        if (this.animationTimer !== null) {
            window.clearTimeout(this.animationTimer);
            this.animationTimer = null;
        }
        if (animate) {
            const transition = [
                `flex ${duration}ms ${easing}`,
                `margin ${duration}ms ${easing}`,
                `width ${duration}ms ${easing}`,
                `border-width ${duration}ms ${easing}`,
                `padding ${duration}ms ${easing}`,
                `opacity ${duration}ms ${easing}`
            ].join(', ');
            for (const element of animated) {
                element.style.transition = transition;
            }
        }
        if (open) {
            this.shell.classList.add('docs-open');
            this.view.setAttribute('aria-hidden', 'false');
            void this.view.offsetWidth;
            this.view.style.flex = `0 0 ${this.storedWidth()}px`;
        }
        else {
            this.view.style.flex = '0 0 0px';
            this.shell.classList.remove('docs-open');
            this.view.setAttribute('aria-hidden', 'true');
        }
        const finish = () => {
            this.animationTimer = null;
            for (const element of animated) {
                element.style.transition = '';
            }
        };
        if (animate) {
            this.animationTimer = window.setTimeout(finish, duration);
        }
        else {
            finish();
        }
        if (persist) {
            this.persistLayout();
        }
    }
    persistLayout() {
        if (window.resizableViews) {
            window.resizableViews.saveLayout();
            return;
        }
        saveWindowUiFromDom();
    }
    readSavedOpen() {
        return isDocsOpen();
    }
    async loadPersistedPage() {
        await this.ensureManifest();
        const targetId = this.readLastPageId() ||
            this.manifest?.defaultId ||
            this.pages.keys().next().value;
        if (!targetId) {
            return;
        }
        await this.showPage(targetId);
        this.scrollCurrentTocIntoView();
    }
    storedWidth() {
        return Math.max(MIN_WIDTH, getDocsWidthPx() || DEFAULT_WIDTH);
    }
    readLastPageId() {
        const stored = getDocsPageId();
        if (stored && this.pages.has(stored)) {
            return stored;
        }
        return null;
    }
    async ensureManifest() {
        if (this.manifest) {
            return;
        }
        if (!this.loadPromise) {
            this.loadPromise = this.loadManifest();
        }
        await this.loadPromise;
    }
    async loadManifest() {
        const response = await fetch(handbookUrl('manifest.json'));
        if (!response.ok) {
            throw new Error('Failed to load documentation manifest');
        }
        this.manifest = (await response.json());
        this.pages = new Map();
        flattenPages(this.manifest.nodes, this.pages);
        this.renderToc();
    }
    renderToc() {
        if (!this.manifest) {
            return;
        }
        this.tocEl.replaceChildren(this.renderTocList(this.manifest.nodes));
    }
    renderTocList(nodes) {
        const list = document.createElement('ul');
        list.className = 'docs-toc-list';
        for (const node of nodes) {
            const item = document.createElement('li');
            if (node.kind === 'section') {
                const details = document.createElement('details');
                details.open = true;
                const summary = document.createElement('summary');
                summary.textContent = node.title;
                details.appendChild(summary);
                if (node.path) {
                    summary.classList.add('docs-toc-link');
                    summary.dataset.docsId = node.id;
                    summary.addEventListener('click', (event) => {
                        event.preventDefault();
                        details.open = !details.open;
                        void this.open(node.id);
                    });
                }
                if (node.children && node.children.length > 0) {
                    details.appendChild(this.renderTocList(node.children));
                }
                item.appendChild(details);
            }
            else {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'docs-toc-link';
                button.dataset.docsId = node.id;
                button.textContent = node.title;
                button.addEventListener('click', () => {
                    void this.open(node.id);
                });
                item.appendChild(button);
            }
            list.appendChild(item);
        }
        return list;
    }
    highlightToc() {
        this.tocEl.querySelectorAll('.docs-toc-link').forEach((el) => {
            el.classList.toggle('is-current', el.getAttribute('data-docs-id') === this.currentId);
        });
        const current = this.tocEl.querySelector('.docs-toc-link.is-current');
        let ancestor = current;
        while (ancestor && ancestor !== this.tocEl) {
            if (ancestor instanceof HTMLDetailsElement) {
                ancestor.open = true;
            }
            ancestor = ancestor.parentElement;
        }
        this.scrollCurrentTocIntoView();
    }
    scrollCurrentTocIntoView() {
        const current = this.tocEl.querySelector('.docs-toc-link.is-current');
        if (!(current instanceof HTMLElement)) {
            return;
        }
        current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    async showPage(id, heading) {
        const page = this.pages.get(id);
        if (!page) {
            this.articleEl.textContent = `Unknown documentation page: ${id}`;
            return;
        }
        this.currentId = id;
        setDocsPageId(id);
        this.highlightToc();
        const markdown = await this.fetchText(handbookUrl(page.path));
        this.articleEl.innerHTML = this.renderMarkdown(markdown, page.path);
        this.rewriteMedia(page.path);
        this.highlightCodeBlocks();
        if (heading) {
            const prefixed = docsHeadingId(heading);
            const target = this.articleEl.querySelector(`#${CSS.escape(prefixed)}`) ||
                this.articleEl.querySelector(`#${CSS.escape(heading)}`);
            target?.scrollIntoView({ block: 'start' });
        }
        else {
            this.articleEl.scrollTop = 0;
        }
    }
    renderMarkdown(markdown, relativePath) {
        const localized = localizeModifierNotation(markdown);
        if (typeof marked === 'undefined') {
            return localized;
        }
        const html = marked.parse(localized, { gfm: true, breaks: false });
        const htmlText = typeof html === 'string' ? html : String(html);
        const wrap = document.createElement('div');
        wrap.innerHTML = htmlText;
        wrap.querySelectorAll('h1, h2, h3, h4').forEach((heading) => {
            heading.id = docsHeadingId(heading.id || slugify(heading.textContent || ''));
        });
        wrap.querySelectorAll('img').forEach((img) => {
            const src = img.getAttribute('src');
            if (src) {
                img.setAttribute('src', this.resolveAssetSrc(src, relativePath));
            }
        });
        wrap.querySelectorAll('a').forEach((anchor) => {
            const href = anchor.getAttribute('href');
            if (!href) {
                return;
            }
            const resolved = this.resolveLink(href, relativePath);
            if (resolved.kind === 'external') {
                anchor.setAttribute('target', '_blank');
                anchor.setAttribute('rel', 'noopener noreferrer');
                return;
            }
            if (resolved.kind === 'page') {
                anchor.setAttribute('href', buildDocsHref(resolved.id, resolved.heading));
                anchor.dataset.docsId = resolved.id;
                if (resolved.heading) {
                    anchor.dataset.docsHeading = resolved.heading;
                }
            }
        });
        wrap.querySelectorAll('code').forEach((code) => {
            if (code.parentElement?.tagName === 'PRE') {
                return;
            }
            const spec = shortcutSpecFromHandbook(code.textContent || '');
            if (spec === null) {
                return;
            }
            code.classList.add('docs-shortcut-chip');
            code.innerHTML = keyboardShortcutHtml(spec);
        });
        wrap.querySelectorAll('pre > code').forEach((code) => {
            const hasLanguage = [...code.classList].some((cls) => cls.startsWith('language-') || cls.startsWith('lang-'));
            if (!hasLanguage) {
                code.classList.add('language-python');
            }
        });
        return wrap.innerHTML;
    }
    highlightCodeBlocks() {
        const blocks = this.articleEl.querySelectorAll('pre');
        const ace = window.ace;
        if (!ace?.require || blocks.length === 0) {
            return;
        }
        let highlighter;
        let theme;
        let PythonMode;
        try {
            highlighter = ace.require('ace/ext/static_highlight');
            const themePath = document.documentElement.getAttribute('data-theme') === 'light'
                ? 'ace/theme/tomorrow'
                : 'ace/theme/tomorrow_night';
            theme = ace.require(themePath);
            PythonMode = ace.require('ace/mode/python').Mode;
        }
        catch (error) {
            console.warn('Ace static highlight is unavailable', error);
            return;
        }
        if (!highlighter?.renderSync || !PythonMode) {
            return;
        }
        blocks.forEach((pre) => {
            if (!(pre instanceof HTMLElement)) {
                return;
            }
            const code = pre.querySelector('code');
            const source = pre.dataset.docsSource ??
                code?.textContent ??
                pre.textContent ??
                '';
            pre.dataset.docsSource = source;
            const result = highlighter.renderSync(source.replace(/\n$/, ''), new PythonMode(), theme, 1, true);
            this.applyAceHighlightCss(result.css);
            pre.classList.add('docs-code-block');
            pre.innerHTML = result.html;
        });
    }
    applyAceHighlightCss(css) {
        let style = document.getElementById('docs-ace-highlight-css');
        if (!style) {
            style = document.createElement('style');
            style.id = 'docs-ace-highlight-css';
            document.head.appendChild(style);
        }
        style.textContent = css;
    }
    rewriteMedia(relativePath) {
        this.articleEl.querySelectorAll('img').forEach((img) => {
            const resolved = img.getAttribute('src');
            if (resolved) {
                img.dataset.docsSrc = canonicalHandbookImageSrc(resolved);
            }
            img.addEventListener('error', () => {
                const canonical = img.dataset.docsSrc;
                const current = img.getAttribute('src');
                if (canonical && current && current !== canonical) {
                    img.setAttribute('src', canonical);
                    return;
                }
                img.classList.add('docs-image-missing');
            });
            void relativePath;
        });
        this.applyThemedImages();
    }
    applyThemedImages() {
        this.articleEl.querySelectorAll('img').forEach((img) => {
            const canonical = img.dataset.docsSrc ||
                canonicalHandbookImageSrc(img.getAttribute('src') || '');
            if (!canonical) {
                return;
            }
            img.dataset.docsSrc = canonical;
            const next = themedHandbookImageSrc(canonical);
            if (img.getAttribute('src') !== next) {
                img.classList.remove('docs-image-missing');
                img.setAttribute('src', next);
            }
        });
    }
    resolveAssetSrc(href, currentPath) {
        if (/^(https?:|data:|\/)/i.test(href)) {
            return href;
        }
        const currentDir = currentPath.includes('/')
            ? currentPath.slice(0, currentPath.lastIndexOf('/'))
            : '';
        const joined = currentDir ? `${currentDir}/${href}` : href;
        const normalized = joined.split('/').reduce((parts, part) => {
            if (part === '.' || part === '') {
                return parts;
            }
            if (part === '..') {
                parts.pop();
                return parts;
            }
            parts.push(part);
            return parts;
        }, []);
        return handbookUrl(normalized.join('/'));
    }
    resolveLink(href, currentPath) {
        if (href.startsWith('http://') ||
            href.startsWith('https://') ||
            href.startsWith('mailto:')) {
            return { kind: 'external' };
        }
        if (href.startsWith('#')) {
            return {
                kind: 'page',
                id: this.currentId || '',
                heading: href.slice(1)
            };
        }
        const [rawPath, hash] = href.split('#');
        if (!rawPath.toLowerCase().endsWith('.md')) {
            return { kind: 'other' };
        }
        const resolvedSrc = this.resolveAssetSrc(rawPath, currentPath).replace(`${HANDBOOK_ROOT}/`, '');
        for (const page of this.pages.values()) {
            if (page.path === resolvedSrc ||
                page.path.replace(/\.md$/i, '') ===
                    resolvedSrc.replace(/\.md$/i, '')) {
                return { kind: 'page', id: page.id, heading: hash };
            }
        }
        return { kind: 'other' };
    }
    onArticleClick(event) {
        const target = event.target?.closest('a');
        if (!target || !this.articleEl.contains(target)) {
            return;
        }
        const id = target.dataset.docsId;
        if (!id) {
            return;
        }
        event.preventDefault();
        void this.open(id, target.dataset.docsHeading);
    }
    async fetchText(url) {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to load ${url}`);
        }
        return await response.text();
    }
}
export function initDocsViewer() {
    try {
        const viewer = new DocsViewer();
        window.docsViewer = viewer;
        window.openDocs = (id, heading) => {
            return viewer.open(id, heading);
        };
        window.closeDocs = () => viewer.close();
    }
    catch (error) {
        console.error('Documentation viewer failed to initialize', error);
    }
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDocsViewer);
}
else {
    initDocsViewer();
}
